'use client';

// @TODO: Refactor this page. It's a mess.
import './sac-controls.scss';

import {
  ReadonlyURLSearchParams,
  usePathname,
  useRouter,
  useSearchParams,
} from 'next/navigation';
import { Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { throttle } from 'throttle-debounce';

import { ButtonStyle } from '@sola/ark-core/atoms/button-style/button-style.js';
import { Link } from '@sola/ark-core/atoms/link/link.js';
import { ChipGroup } from '@sola/ark-core/molecules/chip-group/chip-group.js';
import { RadioGroup } from '@sola/ark-core/molecules/radio-group/radio-group.js';
import { Select } from '@sola/ark-core/molecules/select/select.js';
import { SliderInput } from '@sola/ark-core/molecules/slider-input/slider-input.js';
import { cn } from '@sola/ark-core/utils/helpers.js';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.js';
import { generalConfig } from '@sola/config/lib/getters.js';
import {
  countryIsoToRegionIso,
  programCountryIsoCodes,
} from '@sola/contentful-model/lib/sac-values.js';
import {
  TrackChildSearch,
  trackClick,
} from '@sola/cookies/components/datalayer.js';
import { ChildGenderEnum } from '@sola/schema/dotcom/children/childUtilityInterfaces.js';
import { IconNames } from '@sola/schema/lib/icons/icon-names.js';
import { SAC_URL_PARAMS, SacFilterProps } from '@sola/schema/lib/schema.js';

import { birthdayFromDate, getCleanParams, safeNumber } from '../../utils/sac';
import { getBaseSizeProps } from '../../utils/text-utils';
import { FeatureBase } from '../feature-base/feature-base';

const { sacMinAge, sacMaxAge } = generalConfig;

type urlParams = {
  name: string;
  value?: string;
}[];

type SacControlsProps = SacFilterProps & {
  variant?: 'default' | 'stacked';
  isLive?: boolean;
};

export const generateMonthItems = () =>
  [...Array(12).keys()]
    .map((v) => v + 1)
    .map((v) => {
      return {
        value: v.toString(),
        // TODO: es
        text: Intl.DateTimeFormat('en', { month: 'long' }).format(
          new Date(2000, v - 1),
        ),
      };
    });

export const generateDayItems = () =>
  [...Array(31).keys()]
    .map((v) => v + 1)
    .map((v) => {
      return {
        value: v.toString(),
        text: v.toString(),
      };
    });

const InternalSacControls: React.FC<
  SacControlsProps & { searchParams?: ReadonlyURLSearchParams }
> = ({
  ageMin,
  ageMax,
  gender,
  countries,
  orphan,
  disability,
  highlyVulnerable,
  hiv,
  birthDate,
  searchParams,
  variant = 'default',
  isLive,
  ...props
}) => {
  const { t } = useTranslation();

  const [filters, setFilters] = useState(
    [
      orphan && SAC_URL_PARAMS.ORPHANED_CHILD,
      disability && SAC_URL_PARAMS.HAS_DISABILITIES,
      highlyVulnerable && SAC_URL_PARAMS.VULNERABLE_EXPLOITATION,
      hiv && SAC_URL_PARAMS.AFFECTED_HIV,
    ].filter(Boolean) as SAC_URL_PARAMS[],
  );

  const router = useRouter();
  const pathname = usePathname();

  const chips = [
    {
      name: t('skippy.sac.all'),
      value: '',
      isClear: true,
    },
    {
      name: t('skippy.sac.orphanedChild'),
      value: SAC_URL_PARAMS.ORPHANED_CHILD,
      icon: 'child' as IconNames,
    },
    {
      name: t('skippy.sac.hasDisabilities'),
      value: SAC_URL_PARAMS.HAS_DISABILITIES,
      icon: 'wheelchair' as IconNames,
    },
    {
      name: t('skippy.sac.vulnerableToExploitation'),
      value: SAC_URL_PARAMS.VULNERABLE_EXPLOITATION,
      icon: 'alert-triangle' as IconNames,
    },
    {
      name: t('skippy.sac.affectedByHiv'),
      value: SAC_URL_PARAMS.AFFECTED_HIV,
      icon: 'virus' as IconNames,
    },
  ];

  const params = useMemo(
    () => new URLSearchParams(searchParams?.toString()),
    [searchParams],
  );

  const [showAdvanced, setShowAdvanced] = useState<boolean>(
    params.has(SAC_URL_PARAMS.GENDER) ||
      params.has(SAC_URL_PARAMS.AGE_MIN) ||
      params.has(SAC_URL_PARAMS.AGE_MAX) ||
      params.has(SAC_URL_PARAMS.COUNTRIES),
  );

  const [countriesParams, setCountriesParams] = useState(
    params.get(SAC_URL_PARAMS.COUNTRIES) ?? '',
  );

  const [isMultipleCountry, setIsMultipleCountry] = useState(
    (params.get(SAC_URL_PARAMS.COUNTRIES)?.split(',') ?? '').length > 1,
  );

  useEffect(() => {
    const raw = params.get(SAC_URL_PARAMS.COUNTRIES) ?? '';
    const list = raw ? raw.split(',').filter(Boolean) : [];
    setCountriesParams(raw);
    setIsMultipleCountry(list.length > 1);
  }, [params]);

  useEffect(() => {
    const paramFilters = params.get(SAC_URL_PARAMS.FILTERS);
    if (paramFilters) {
      setFilters(paramFilters.split(',') as SAC_URL_PARAMS[]);
    } else {
      setFilters([]);
    }
  }, [params]);

  const createQueryString = useCallback(
    (param: urlParams) => {
      param.forEach((p) => {
        if (!p.value) {
          params.delete(p.name);
        } else if (p.value === 'Either') {
          params.delete(p.name);
        } else {
          params.set(p.name, p.value);
        }
      });

      return params.toString();
    },
    [params],
  );

  const getGenderOptions = () => [
    {
      value: ChildGenderEnum.Male,
      text: t('skippy.sac.controls.boy'),
    },
    {
      value: ChildGenderEnum.Female,
      text: t('skippy.sac.controls.girl'),
    },
    { value: ChildGenderEnum.Either, text: t('skippy.sac.controls.either') },
  ];

  const getCountries = () => [
    { value: '', text: t('skippy.sac.controls.allCountries') },
    ...(isMultipleCountry
      ? [
          {
            value: 'multiple',
            text: t('skippy.sac.controls.multipleCountries'),
            disabled: true,
          },
        ]
      : []),
    ...programCountryIsoCodes
      .map((c) => {
        return {
          value: c,
          text: t(`geo.countries.${c}.nameS` as 'geo.countries.BD.nameS'),
        };
      })
      .sort((a, b) => a.value.localeCompare(b.value)),
  ];

  const getMonths = () => [
    { value: '', text: t('skippy.sac.controls.month') },
    ...generateMonthItems(),
  ];

  const getDays = () => [
    { value: '', text: t('skippy.sac.controls.day') },
    ...generateDayItems(),
  ];

  const throttledSliderHandler = throttle(
    300,
    (minAge: string, maxAge: string) => {
      router.replace(
        `${pathname}?${createQueryString([
          { name: SAC_URL_PARAMS.AGE_MIN, value: minAge },
          { name: SAC_URL_PARAMS.AGE_MAX, value: maxAge },
        ])}`,
        { scroll: false },
      );
    },
    { noLeading: true },
  );

  useEffect(() => {
    if (ageMin && !params.has(SAC_URL_PARAMS.AGE_MIN)) {
      params.set(SAC_URL_PARAMS.AGE_MIN, `${ageMin}`);
    }
    if (ageMax && !params.has(SAC_URL_PARAMS.AGE_MAX)) {
      params.set(SAC_URL_PARAMS.AGE_MAX, `${ageMax}`);
    }
    if (gender && !params.has(SAC_URL_PARAMS.GENDER)) {
      params.set(SAC_URL_PARAMS.GENDER, gender);
    }
    if (filters.length && !params.has(SAC_URL_PARAMS.FILTERS)) {
      params.set(SAC_URL_PARAMS.FILTERS, filters.join(','));
    }
    if (countries?.length && !params.has(SAC_URL_PARAMS.COUNTRIES)) {
      params.set(SAC_URL_PARAMS.COUNTRIES, countries.join(','));
    }
    const { day, month } = birthdayFromDate(birthDate);
    if (day && !params.has(SAC_URL_PARAMS.BIRTH_DAY)) {
      params.set(SAC_URL_PARAMS.BIRTH_DAY, day);
    }
    if (month && !params.has(SAC_URL_PARAMS.BIRTH_MONTH)) {
      params.set(SAC_URL_PARAMS.BIRTH_MONTH, month);
    }
    if (searchParams?.toString() !== params.toString()) {
      router.replace(`${pathname}?${params.toString()}`, { scroll: false });
    }
  }, []);

  const getSacControls = (className?: string) => (
    <div
      className={`SacControls__controls-container p-comfy-xl grid grid-cols-1 md:grid-cols-3 gap-comfy-lg ${className}`}
    >
      <span>
        {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
        <label id="gender-group-label" className="form-label label">
          {t('skippy.sac.wantToSponsor')}
        </label>
        <RadioGroup
          defaultValue={
            params.get(SAC_URL_PARAMS.GENDER) ?? ChildGenderEnum.Either
          }
          aria-label={t('skippy.sac.controls.chooseGender')}
          loop={false}
          variant="buttons"
          name="gender"
          aria-labelledby="gender-group-label"
          onChange={(v) => {
            router.replace(
              `${pathname}?${createQueryString([{ name: SAC_URL_PARAMS.GENDER, value: v?.target?.value }])}`,
              { scroll: false },
            );
          }}
          items={getGenderOptions()}
        />
      </span>
      <span>
        <SliderInput
          value={[
            safeNumber(params.get(SAC_URL_PARAMS.AGE_MIN)) ??
              (ageMin || sacMinAge),
            safeNumber(params.get(SAC_URL_PARAMS.AGE_MAX)) ??
              (ageMax || sacMaxAge),
          ]}
          label={t('skippy.sac.betweenTheAges')}
          min={sacMinAge}
          max={sacMaxAge}
          name="age"
          showThumbLabels
          onCommit={(sliderRange: { newValue: number[] }) => {
            throttledSliderHandler(
              sliderRange?.newValue[0].toString(),
              sliderRange?.newValue[1].toString(),
            );
          }}
        />
      </span>
      <span>
        <Select
          value={isMultipleCountry ? 'multiple' : countriesParams}
          label={t('skippy.sac.whoLivesIn')}
          name="country"
          onChange={(v) => {
            setIsMultipleCountry(false);
            router.replace(
              `${pathname}?${createQueryString([
                {
                  name: SAC_URL_PARAMS.COUNTRIES,
                  value: v?.target?.value,
                },
              ])}`,
              { scroll: false },
            );
          }}
          items={getCountries()}
        />
      </span>
    </div>
  );
  // TODO: Add a button to reset the date
  const getBirthdayControls = () => (
    <div className="flex flex-col md:flex-row items-center justify-center p-comfy-lg gap-comfy-sm">
      <ArkIcon
        iconName="gift-01"
        className="SacControls__birthday-icon hidden md:block"
      />
      <div className="label-md label-moderate">
        {t('skippy.sac.birthdaySearch')}
      </div>
      <span className="flex gap-comfy-sm">
        <Select
          value={params.get(SAC_URL_PARAMS.BIRTH_MONTH) ?? ''}
          name="birth month"
          aria-label={t('skippy.sac.controls.month')}
          onChange={(v) => {
            router.replace(
              `${pathname}?${createQueryString([
                {
                  name: SAC_URL_PARAMS.BIRTH_MONTH,
                  value: v?.target?.value,
                },
              ])}`,
              { scroll: false },
            );
          }}
          items={getMonths()}
        />
        <Select
          value={params.get(SAC_URL_PARAMS.BIRTH_DAY) ?? ''}
          name="birth day"
          aria-label={t('skippy.sac.controls.day')}
          onChange={(v) => {
            router.replace(
              `${pathname}?${createQueryString([
                {
                  name: SAC_URL_PARAMS.BIRTH_DAY,
                  value: v?.target?.value,
                },
              ])}`,
              { scroll: false },
            );
          }}
          items={getDays()}
        />
      </span>
    </div>
  );

  const getAdvancedOptions = () => (
    <div className="SacControls__adv-search-container block md:hidden flex flex-col p-comfy-xs md:p-comfy-sm justify-center">
      {showAdvanced && getSacControls()}
      <ButtonStyle
        variant="tertiary"
        fullWidth
        iconAfter={
          showAdvanced ? (
            <ArkIcon iconName="chevron-up" />
          ) : (
            <ArkIcon iconName="chevron-down" />
          )
        }
      >
        <button
          type="button"
          onClick={() => {
            trackClick(t('skippy.sac.advancedSearch'));
            setShowAdvanced((v) => !v);
          }}
        >
          {t('skippy.sac.advancedSearch')}
        </button>
      </ButtonStyle>
    </div>
  );

  const showChips = variant !== 'stacked';

  return (
    // TODO: this layout isn't exactly right as the spacing is a bit off
    <div
      className={cn(
        `SacControls SacControls--${variant} flex flex-col gap-roomy-xs`,
      )}
      aria-label={t('skippy.sac.controls.legend')}
      role="group"
    >
      <TrackChildSearch
        event={{
          gender: params.get(SAC_URL_PARAMS.GENDER) ?? undefined,
          ageMin:
            safeNumber(params.get(SAC_URL_PARAMS.AGE_MIN)) ??
            (ageMin || undefined),
          ageMax:
            safeNumber(params.get(SAC_URL_PARAMS.AGE_MAX)) ??
            (ageMax || undefined),
          region: countryIsoToRegionIso(countriesParams.split(',')[0]),
          country: countriesParams,
          birthMonth: params.get(SAC_URL_PARAMS.BIRTH_MONTH)
            ? Number(params.get(SAC_URL_PARAMS.BIRTH_MONTH))
            : undefined,
          birthDay: params.get(SAC_URL_PARAMS.BIRTH_DAY)
            ? Number(params.get(SAC_URL_PARAMS.BIRTH_DAY))
            : undefined,
          orphan:
            filters.some((f) => f === SAC_URL_PARAMS.ORPHANED_CHILD) ||
            undefined,
          disability:
            filters.some((f) => f === SAC_URL_PARAMS.HAS_DISABILITIES) ||
            undefined,
          highlyVulnerable:
            filters.some((f) => f === SAC_URL_PARAMS.VULNERABLE_EXPLOITATION) ||
            undefined,
          hiv:
            filters.some((f) => f === SAC_URL_PARAMS.AFFECTED_HIV) || undefined,
        }}
      />
      <div className="SacControls__card flex flex-col">
        {variant !== 'stacked' && (
          <>
            {getSacControls('hidden md:grid')}
            {getBirthdayControls()}
            {getAdvancedOptions()}
          </>
        )}
        {variant === 'stacked' && (
          <>
            <FeatureBase
              {...props}
              {...getBaseSizeProps(2)}
              {...{
                primaryCTA: undefined,
                secondaryCTA: undefined,
                tertiaryCTA: undefined,
              }}
              className="p-comfy-xl pb-none"
              inline
            />
            {getSacControls('flex flex-col')}
          </>
        )}
        {!isLive && (
          <div className="p-comfy-xl">
            <ButtonStyle variant="primary">
              <Link href={`/sponsor-a-child?${getCleanParams(params)}`}>
                {t('core.actions.sponsorAChild')}
              </Link>
            </ButtonStyle>
          </div>
        )}
      </div>
      {showChips && (
        <ChipGroup
          chips={chips}
          selectedValues={filters.length ? filters : ['']}
          handleSelectionChange={(v) => {
            router.replace(
              `${pathname}?${createQueryString([
                {
                  name: SAC_URL_PARAMS.FILTERS,
                  value: v.join(','),
                },
              ])}`,
              { scroll: false },
            );
            setFilters(v as SAC_URL_PARAMS[]);
          }}
          isScrollable
        />
      )}
    </div>
  );
};

export const LiveSacControls: React.FC<SacControlsProps> = (props) => {
  const searchParams = useSearchParams();
  return <InternalSacControls {...props} searchParams={searchParams} />;
};

// During SSG we render the controls without caring about search params,
// so we can deliver the block quickly. Then, when Suspense finishes,
// we update it with the search params on the client.
export const SacControls: React.FC<SacControlsProps> = (props) => {
  return (
    <Suspense fallback={<InternalSacControls {...props} />}>
      <LiveSacControls {...props} />
    </Suspense>
  );
};
